import React, { useMemo, useState } from 'react';
import { bonusText, fmtScore, fmtValue, money, MONTHS } from './kpiFormat';
import './KPICard.css';

/*
 * «Карта KPI» — то, что управляющий отправляет должности: цели на месяц
 * (до начала) и итоги с баллами и премиями (после). Текст для мессенджера
 * копируется одной кнопкой, печать открывает чистую страницу — её же можно
 * сохранить в PDF из диалога печати. Цели берутся из целей этого месяца.
 */

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');

const KPICard = ({ role, employees, period, minScore, onClose }) => {
  const [mode, setMode] = useState('goals');
  const [copied, setCopied] = useState(false);

  const periodText = `${MONTHS[period.month - 1].toLowerCase()} ${period.year}`;
  // Сначала самые весомые показатели — на них сотрудник и должен смотреть.
  const rows = useMemo(() => role.metrics
    .filter(m => m.goal)
    .sort((a, b) => b.goal.weight - a.goal.weight)
    .map(m => ({ ...m, v10: m.goal.thresholds[0], v7: m.goal.thresholds[3], v1: m.goal.thresholds[9] })), [role]);

  const level = (r, v) => {
    if (v === null || v === undefined) return '—';
    // «не больше 0» читается странно — для нулевой цели пишем просто «0».
    if (r.goal.lower && v === 0) return fmtValue(r.unit, v);
    return `${r.goal.lower ? 'не больше' : 'от'} ${fmtValue(r.unit, v)}`;
  };
  const tiers = [...role.bonus.tiers].sort((a, b) => b.min_score - a.min_score);

  const goalsText = [
    `KPI на ${periodText} — ${role.name}`,
    '',
    ...rows.map(r => `• ${r.name} (вес ${Math.round(r.goal.weight * 100)}%): цель ${level(r, r.v10)}, для премии ${level(r, r.v7)}`),
    '',
    'Премия по итоговому баллу:',
    ...tiers.map(t => `• ${String(t.min_score).replace('.', ',')} и выше — ${bonusText(role.bonus, t.amount)}`),
    `Ниже ${minScore} баллов премия не начисляется.`,
    '',
    'Итоговый балл — сумма «вес × балл» по показателям (от 1 до 10).',
  ].join('\n');

  const resultsText = [
    `Итоги KPI за ${periodText} — ${role.name}`,
    '',
    ...employees.map(e => (e.result.complete
      ? `• ${e.name}: ${fmtScore(e.result.score)} балла, премия ${money(e.result.bonus)}`
      : `• ${e.name}: факт заполнен не полностью`)),
    ...(employees.length ? [] : ['Сотрудники не добавлены.']),
    '',
    'Факт по показателям:',
    ...rows.map(r => {
      const vals = employees.map(e => e.metrics?.[r.key] ?? null);
      const uniq = [...new Set(vals.map(v => fmtValue(r.unit, v)))];
      return `• ${r.name}: ${uniq.length <= 1 ? (uniq[0] || '—')
        : employees.map((e, i) => `${e.name.split(' ')[0]} ${fmtValue(r.unit, vals[i])}`).join(', ')}`;
    }),
  ].join('\n');

  const text = mode === 'goals' ? goalsText : resultsText;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Скопируйте текст', text);
    }
  };

  const print = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    const body = mode === 'goals'
      ? `<table><thead><tr><th>Показатель</th><th>Вес</th><th>Цель (10 баллов)</th><th>Для премии (7 баллов)</th><th>1 балл</th></tr></thead><tbody>${
        rows.map(r => `<tr><td>${esc(r.name)}</td><td>${Math.round(r.goal.weight * 100)}%</td><td>${esc(level(r, r.v10))}</td><td>${esc(level(r, r.v7))}</td><td>${esc(fmtValue(r.unit, r.v1))}</td></tr>`).join('')
      }</tbody></table><h3>Премия по итоговому баллу</h3><ul>${
        tiers.map(t => `<li>${String(t.min_score).replace('.', ',')} и выше — ${esc(bonusText(role.bonus, t.amount))}</li>`).join('')
      }<li>Ниже ${minScore} баллов премия не начисляется</li></ul><p class="sign">С целями ознакомлен(а): ______________________ / ______________ / дата ________</p>`
      : `<table><thead><tr><th>Сотрудник</th><th>Балл</th><th>Премия</th></tr></thead><tbody>${
        employees.map(e => `<tr><td>${esc(e.name)}</td><td>${esc(fmtScore(e.result.score))}</td><td>${esc(money(e.result.bonus))}</td></tr>`).join('')
      }</tbody></table>`;
    w.document.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8"><title>${esc(mode === 'goals' ? 'KPI' : 'Итоги KPI')} — ${esc(role.name)}, ${esc(periodText)}</title>
      <style>body{font:14px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:32px}h1{font-size:20px;margin:0 0 4px}
      .sub{color:#555;margin-bottom:20px}table{border-collapse:collapse;width:100%}th,td{border:1px solid #ccc;padding:8px 10px;text-align:left}
      th{background:#f3f3f3}h3{margin:24px 0 8px;font-size:15px}.sign{margin-top:40px}</style></head><body>
      <h1>${esc(mode === 'goals' ? 'KPI' : 'Итоги KPI')}: ${esc(role.name)}</h1><div class="sub">Период: ${esc(periodText)}</div>${body}
      <script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="ui-modal" onClick={onClose}>
      <div className="ui-modal-box kpi-card-box" onClick={e => e.stopPropagation()}>
        <div className="ui-modal-head">
          <h2>{role.name}: {periodText}</h2>
          <button className="ui-icon-btn" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className="ui-tabs kpi-card-tabs">
          <button type="button" className={`ui-tab ${mode === 'goals' ? 'active' : ''}`} onClick={() => setMode('goals')}>Цели</button>
          <button type="button" className={`ui-tab ${mode === 'results' ? 'active' : ''}`} onClick={() => setMode('results')}>Итоги</button>
        </div>

        {mode === 'goals' ? (
          rows.length === 0 ? (
            <p className="kpi-card-empty">Для должности не заданы цели.</p>
          ) : (
            <div className="table-wrapper">
              <table className="kpi-card-table">
                <thead><tr><th>Показатель</th><th>Вес</th><th>Цель · 10</th><th>Премия · 7</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key}>
                      <td>{r.name}</td>
                      <td>{Math.round(r.goal.weight * 100)}%</td>
                      <td>{level(r, r.v10)}</td>
                      <td>{level(r, r.v7)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="table-wrapper">
            <table className="kpi-card-table">
              <thead><tr><th>Сотрудник</th><th>Балл</th><th>Премия</th></tr></thead>
              <tbody>
                {employees.length === 0 ? (
                  <tr><td colSpan={3} className="kpi-card-muted">Сотрудники не добавлены</td></tr>
                ) : employees.map(e => (
                  <tr key={e.id}>
                    <td>{e.name}</td>
                    <td>{fmtScore(e.result.score)}</td>
                    <td>{money(e.result.bonus)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {mode === 'goals' && tiers.length > 0 && (
          <div className="kpi-card-bonus">
            <span>Премия:</span>
            {tiers.map(t => (
              <span key={t.min_score} className="kpi-card-chip">
                {String(t.min_score).replace('.', ',')}+ → {bonusText(role.bonus, t.amount)}
              </span>
            ))}
          </div>
        )}

        <details className="kpi-card-preview">
          <summary>Текст для мессенджера</summary>
          <pre>{text}</pre>
        </details>

        <div className="ui-modal-actions">
          <button className="ui-btn-ghost" onClick={print}>Печать / PDF</button>
          <button className="ui-btn-primary" onClick={copy}>{copied ? 'Скопировано' : 'Скопировать текст'}</button>
        </div>
      </div>
    </div>
  );
};

export default KPICard;
